import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChevronRight, CreditCard, Loader2, LogOut, Mail, Pencil, Trash2 } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { AppDispatch, RootState } from '../store'
import { clearUser } from '../store/authSlice'
import { InvitationStatus } from '../types/invitation'
import emailPreferencesService from '../services/emailPreferencesService'
import usersService from '../services/usersService'
import authService from '../services/authService'
import { webSocketService } from '../services/websocket'
import { useTranslation } from '../hooks/use-i18n'
import { authUserId } from '../utils/userProfile'
import LanguageSelector from '../components/LanguageSelector'
import UserAvatar from '../components/UserAvatar'
import EditAccountModal from '../components/EditAccountModal'

const Account: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  const { invitations } = useSelector((state: RootState) => state.invitations)
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showRemove, setShowRemove] = useState(false)
  const [removing, setRemoving] = useState(false)

  const pendingInvites = invitations.filter((inv) => inv.status === InvitationStatus.Pending).length

  const prefsQuery = useQuery({
    queryKey: ['email-preferences'],
    queryFn: () => emailPreferencesService.get(),
    staleTime: 60 * 1000,
  })

  const updateMutation = useMutation({
    mutationFn: (patch: { footballEmailsEnabled?: boolean; formula1EmailsEnabled?: boolean }) =>
      emailPreferencesService.update(patch),
    onSuccess: (data) => {
      queryClient.setQueryData(['email-preferences'], data)
      toast.success(t('account.emailPrefsSaved'))
    },
    onError: () => toast.error(t('account.emailPrefsError')),
    onSettled: () => setSaving(false),
  })

  const handleToggle = (patch: { footballEmailsEnabled?: boolean; formula1EmailsEnabled?: boolean }) => {
    setSaving(true)
    updateMutation.mutate(patch)
  }

  const handleLogout = () => {
    authService.logout()
    dispatch(clearUser())
    webSocketService.disconnect()
    navigate('/')
  }

  const handleRemoveAccount = async () => {
    const id = authUserId(user)
    if (!id) return
    setRemoving(true)
    try {
      await usersService.delete(id)
      authService.logout()
      dispatch(clearUser())
      webSocketService.disconnect()
      toast.success(t('auth.accountRemovedToast'))
      navigate('/')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : t('account.updateFailed'))
    } finally {
      setRemoving(false)
      setShowRemove(false)
    }
  }

  const footballEmailsEnabled = prefsQuery.data?.footballEmailsEnabled ?? true
  const formula1EmailsEnabled = prefsQuery.data?.formula1EmailsEnabled ?? true

  const actionClass =
    'w-full flex items-center gap-3 rounded-2xl border border-border bg-card/60 hover:bg-muted/20 transition-colors p-4 text-left'

  return (
    <div className="min-h-screen bg-background">
      <main className="container max-w-lg mx-auto p-4 pt-20 pb-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('nav.dashboard')}
        </Link>

        <h1 className="font-display text-2xl font-extrabold text-foreground mb-5">{t('nav.account')}</h1>

        <div className="glass-card rounded-2xl p-5 mb-4 flex items-center gap-4">
          <UserAvatar name={user?.name} avatar={user?.avatar} size={64} />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-foreground truncate">{user?.name || 'User'}</h2>
            <p className="text-sm text-muted-foreground truncate mt-0.5">{user?.email}</p>
            <p className="text-sm font-semibold text-emerald-500 mt-2">● {t('account.active')}</p>
          </div>
        </div>

        <section className="glass-card rounded-2xl p-5 mb-4">
          <div className="flex items-start gap-3 mb-4">
            <Mail className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-foreground">{t('account.emailNotifications')}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t('account.emailNotificationsDesc')}</p>
            </div>
          </div>

          {prefsQuery.isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-1">
              <label className="flex items-center justify-between gap-4 py-3 border-t border-border cursor-pointer">
                <span className="text-sm font-medium text-foreground">{t('account.predictionEmails')}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={footballEmailsEnabled}
                  disabled={saving}
                  onClick={() => handleToggle({ footballEmailsEnabled: !footballEmailsEnabled })}
                  className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
                    footballEmailsEnabled ? 'bg-primary' : 'bg-muted'
                  } ${saving ? 'opacity-60' : ''}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-1 ${
                      footballEmailsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
              <label className="flex items-center justify-between gap-4 py-3 border-t border-border cursor-pointer">
                <span className="text-sm font-medium text-foreground">{t('account.formula1Emails')}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formula1EmailsEnabled}
                  disabled={saving}
                  onClick={() => handleToggle({ formula1EmailsEnabled: !formula1EmailsEnabled })}
                  className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
                    formula1EmailsEnabled ? 'bg-primary' : 'bg-muted'
                  } ${saving ? 'opacity-60' : ''}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-1 ${
                      formula1EmailsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>
          )}
        </section>

        <div className="space-y-3">
          <LanguageSelector variant="row" />

          <Link to="/invitations" className={actionClass}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${pendingInvites ? 'bg-destructive/20' : 'bg-sky-500/20'}`}>
              <Mail className={`w-5 h-5 ${pendingInvites ? 'text-red-400' : 'text-sky-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground">{t('nav.invitations')}</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {pendingInvites
                  ? t('account.invitationsPending', { count: String(pendingInvites) })
                  : t('account.invitationsDesc')}
              </p>
            </div>
            {pendingInvites > 0 && (
              <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-extrabold flex items-center justify-center">
                {pendingInvites > 9 ? '9+' : pendingInvites}
              </span>
            )}
            <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </Link>

          <Link to="/payments" className={actionClass}>
            <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground">{t('payments.title')}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{t('payments.subtitle')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </Link>

          <button type="button" onClick={() => setShowEdit(true)} className={actionClass}>
            <div className="w-11 h-11 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Pencil className="w-5 h-5 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground">{t('account.editAccount')}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{t('account.editAccountDesc')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </button>

          <button type="button" onClick={() => setShowRemove(true)} className={actionClass}>
            <div className="w-11 h-11 rounded-xl bg-destructive/20 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground">{t('account.removeAccount')}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{t('account.removeAccountDesc')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </button>

          <button type="button" onClick={handleLogout} className={actionClass}>
            <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <LogOut className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground">{t('nav.signOut')}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{t('account.signOutDesc')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
          </button>
        </div>
      </main>

      {showEdit && <EditAccountModal onClose={() => setShowEdit(false)} />}

      {showRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowRemove(false)}>
          <div className="glass-card rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-foreground font-medium mb-6">{t('account.removeConfirm')}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowRemove(false)}
                className="px-4 py-2 rounded-xl text-muted-foreground hover:bg-muted"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleRemoveAccount}
                disabled={removing}
                className="px-4 py-2 rounded-xl bg-destructive text-destructive-foreground font-medium disabled:opacity-50"
              >
                {removing ? t('common.loading') : t('account.removeAccount')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Account
